import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Filter, RefreshCw, LogIn, Ban, ArrowRight, AlertTriangle, Swords, Handshake, Wand2, ShieldCheck, Crown } from "lucide-react";
import { TripleParlayBuilder } from "./TripleParlayBuilder";
import { toast } from "sonner";
import { api, formatGameTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSlip } from "../context/SlipContext";
import { PropCard } from "./PropCard";
import { AISuggestPanel } from "./AISuggestPanel";
import { HeadToHeadModal } from "./HeadToHeadModal";
import { WinProjection } from "./WinProjection";
import { GameLeanBar, getValueSpot, getConsensus } from "./MarketLean";
import { GoalscorerSection } from "./GoalscorerCard";
import { TeamLogo } from "./TeamLogo";

export const MatchupDetail = ({ matchupId, onBack, onOpenPlayer, onLoaded }) => {
  const { user } = useAuth();
  const { buildParlay } = useSlip();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProps, setLoadingProps] = useState(false);
  const [metric, setMetric] = useState("ALL");
  const [valueOnly, setValueOnly] = useState(false);
  const [agreeOnly, setAgreeOnly] = useState(false);
  const [showTriple, setShowTriple] = useState(false);
  const [showStarters, setShowStarters] = useState(false);
  const [compare, setCompare] = useState([]);
  const [h2hOpen, setH2hOpen] = useState(false);
  const [injuries, setInjuries] = useState(null);
  const [liveStats, setLiveStats] = useState({});

  const fetchDetail = useCallback(() => {
    setLoading(true);
    return api
      .get(`/matchups/${matchupId}`)
      .then((r) => {
        setData(r.data);
        return r.data;
      })
      .finally(() => setLoading(false));
  }, [matchupId]);

  const loadProps = useCallback(async (refresh = false) => {
    setLoadingProps(true);
    try {
      await api.post(`/matchups/${matchupId}/load-props${refresh ? "?refresh=true" : ""}`);
      await fetchDetail();
      onLoaded && onLoaded();
      if (refresh) toast.success("Props refreshed with the latest teams & lines.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load live props for this game.");
    } finally {
      setLoadingProps(false);
    }
  }, [matchupId, fetchDetail, onLoaded]);

  const autoLoaded = useRef(null);
  useEffect(() => {
    fetchDetail().then((d) => {
      if (d && d.source === "live" && !d.props_loaded && user && autoLoaded.current !== matchupId) {
        autoLoaded.current = matchupId;
        loadProps();
      }
    });
    // eslint-disable-next-line
  }, [matchupId, user]);

  useEffect(() => {
    if (!data || (data.source === "live" && !data.props_loaded)) return;
    setInjuries(null);
    api.get(`/matchups/${matchupId}/injuries`).then((r) => setInjuries(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [matchupId, data?.props_loaded, data?.id]);

  // Live prop tracker — current player stat vs line, refreshed every 30s while the game is on
  useEffect(() => {
    let active = true;
    setLiveStats({});
    const fetchLive = () => {
      api
        .get(`/matchups/${matchupId}/live-stats`)
        .then((r) => active && setLiveStats(r.data.live ? r.data.stats || {} : {}))
        .catch(() => {});
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [matchupId]);

  const outIds = new Set(injuries?.out_prop_ids || []);
  const statusMap = injuries?.status_by_player || {};

  const ouProps = useMemo(() => (data?.props || []).filter((p) => !p.market_type), [data]);
  const goalProps = useMemo(() => (data?.props || []).filter((p) => p.market_type), [data]);

  const metrics = useMemo(() => {
    if (!data) return [];
    return ["ALL", ...Array.from(new Set(ouProps.map((p) => p.metric)))];
  }, [data, ouProps]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const out = new Set(injuries?.out_prop_ids || []);
    let list = metric === "ALL" ? ouProps : ouProps.filter((p) => p.metric === metric);
    list = list.filter((p) => !out.has(p.id));
    if (valueOnly) list = list.filter((p) => getValueSpot(p));
    if (agreeOnly) list = list.filter((p) => getConsensus(p));
    return [...list].sort((a, b) => b.edge - a.edge);
  }, [data, ouProps, metric, injuries, valueOnly, agreeOnly]);

  const valueCount = useMemo(() => {
    if (!data) return 0;
    const out = new Set(injuries?.out_prop_ids || []);
    return ouProps.filter((p) => !out.has(p.id) && getValueSpot(p)).length;
  }, [data, ouProps, injuries]);

  const agreeCount = useMemo(() => {
    if (!data) return 0;
    const out = new Set(injuries?.out_prop_ids || []);
    return ouProps.filter((p) => !out.has(p.id) && getConsensus(p)).length;
  }, [data, ouProps, injuries]);

  // One-tap: assemble the strongest AI + Market agreement parlay (prioritise strong + high conviction).
  const buildConsensusParlay = () => {
    const out = new Set(injuries?.out_prop_ids || []);
    const picks = ouProps
      .filter((p) => !out.has(p.id))
      .map((p) => ({ p, c: getConsensus(p) }))
      .filter((x) => x.c);
    if (!picks.length) {
      toast.info("No AI + Market agreement picks in this game yet.");
      return;
    }
    picks.sort(
      (a, b) =>
        (b.c.strong === a.c.strong ? 0 : b.c.strong ? 1 : -1) ||
        b.c.marketPct - a.c.marketPct ||
        (b.p.hit_rate || 0) - (a.p.hit_rate || 0) ||
        (b.p.edge || 0) - (a.p.edge || 0)
    );
    const top = picks.slice(0, 10);
    buildParlay(top.map((x) => ({ prop: x.p, lean: x.c.side })));
  };

  const toggleCompare = (prop) => {
    setCompare((prev) => {
      if (prev.find((p) => p.id === prop.id)) return prev.filter((p) => p.id !== prop.id);
      const next = [...prev, prop].slice(-2);
      if (next.length === 2) setH2hOpen(true);
      return next;
    });
  };

  const closeH2h = () => {
    setH2hOpen(false);
    setCompare([]);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
      </div>
    );
  if (!data) return null;

  return (
    <div>
      <button
        data-testid="back-to-board-button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to slate
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <TeamLogo sport={data.sport} abbr={data.away_abbr} color={data.away_team.color} size="xl" testId="detail-away-logo" />
          <span className="font-heading font-bold text-slate-500">@</span>
          <TeamLogo sport={data.sport} abbr={data.home_abbr} color={data.home_team.color} size="xl" testId="detail-home-logo" />
        </div>
        <div>
          <h1 className="font-heading text-xl font-extrabold text-white">
            {data.away_team.name} <span className="text-slate-500">@</span> {data.home_team.name}
          </h1>
          <p className="font-data text-xs text-slate-400">
            {data.sport} · {formatGameTime(data.commence_time, data.game_time)}
            {data.total ? ` · Spread ${data.spread > 0 ? `+${data.spread}` : data.spread} · O/U ${data.total}` : " · Lines TBD"}
          </p>
        </div>
      </div>

      {data.home_team && data.away_team && data.total ? (
        <div className="rounded-2xl border border-[#233248] bg-[#141C2B] p-4 mb-4">
          <GameLeanBar sport={data.sport} spread={data.spread} away={data.away_team} home={data.home_team} />
          <p className="text-[11px] text-slate-500 mt-2 font-data">Implied by the live spread — which side the market favors.</p>
        </div>
      ) : null}

      {data.home_team && data.away_team && (
        <WinProjection matchupId={matchupId} homeTeam={data.home_team} awayTeam={data.away_team} />
      )}

      {injuries?.ruled_out?.length > 0 && (
        <div data-testid="ruled-out-strip" className="rounded-2xl border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 mb-5">
          <div className="flex items-center gap-2 mb-2.5">
            <Ban className="h-4 w-4 text-[#EF4444]" />
            <h3 className="font-heading font-bold text-white text-sm">Ruled Out — lineup adjusted</h3>
          </div>
          <div className="space-y-2">
            {injuries.ruled_out.map((r) => (
              <div
                key={r.player_id}
                data-testid={`ruled-out-${r.player_id}`}
                className="flex items-center gap-2 flex-wrap text-sm"
              >
                <span className="font-semibold text-slate-300 line-through decoration-2 decoration-[#EF4444]">{r.player_name}</span>
                <span className="font-data text-[11px] text-slate-400">{r.position}</span>
                <span className="rounded-md bg-[#EF4444]/15 text-[#EF4444] text-[10px] font-bold px-1.5 py-0.5 font-data">
                  {r.status}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-600" />
                {r.backup ? (
                  <span className="flex items-center gap-1 text-[#10B981] font-semibold">
                    {r.backup}
                    <span className="font-data text-[10px] text-slate-500">backup in</span>
                  </span>
                ) : (
                  <span className="font-data text-[11px] text-slate-500">backup TBD</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingProps ? (
        <div className="rounded-2xl border border-[#00E5FF]/30 bg-[#141C2B] p-12 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#00E5FF] mx-auto mb-3" />
          <h3 className="font-heading text-white font-bold">Pulling live sportsbook props…</h3>
          <p className="text-sm text-slate-400 mt-1">Fetching real player prop lines for this game.</p>
        </div>
      ) : data.source === "live" && !data.props_loaded && !user ? (
        <div className="rounded-2xl border border-dashed border-[#233248] bg-[#141C2B]/50 p-10 text-center">
          <LogIn className="h-7 w-7 text-slate-500 mx-auto mb-3" />
          <h3 className="font-heading text-white font-bold">Sign in to load live props</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Pulling real player props for a game uses your API credits, so it's available once you sign in.
          </p>
          <button
            data-testid="signin-to-load-button"
            onClick={() => nav("/login")}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#00E5FF] text-[#0B0F17] font-semibold px-4 py-2 text-sm hover:bg-[#33EaFF]"
          >
            <LogIn className="h-4 w-4" /> Sign in
          </button>
        </div>
      ) : data.props.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#233248] bg-[#141C2B]/50 p-10 text-center">
          <h3 className="font-heading text-white font-bold">No player props posted yet</h3>
          <p className="text-sm text-slate-400 mt-1">
            Sportsbooks haven't opened player props for this game yet. Check back closer to game time, or try another matchup.
          </p>
          {data.source === "live" && (
            <button
              data-testid="reload-props-button"
              onClick={() => loadProps(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#233248] text-slate-200 px-4 py-2 text-sm hover:border-[#00E5FF]/50"
            >
              <RefreshCw className="h-4 w-4" /> Re-check props
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 order-2 lg:order-1">
            <AISuggestPanel matchupId={matchupId} props={ouProps} />
          </div>

          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Filter className="h-4 w-4 text-slate-500" />
              {metrics.map((m) => (
                <button
                  key={m}
                  data-testid={`metric-filter-${m.split(" ")[0].toLowerCase()}`}
                  onClick={() => setMetric(m)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    metric === m ? "bg-[#00E5FF] text-[#0B0F17]" : "bg-[#141C2B] border border-[#233248] text-slate-300"
                  }`}
                >
                  {m}
                </button>
              ))}
              {data.source === "live" && (
                <button
                  data-testid="refresh-props-button"
                  onClick={() => loadProps(true)}
                  title="Re-pull the latest teams, positions & lines (uses 1 API credit)"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium bg-[#141C2B] border border-[#233248] text-slate-300 hover:border-[#00E5FF]/50"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              )}
            </div>
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <button
                data-testid="value-spots-toggle"
                onClick={() => { setValueOnly((v) => !v); setAgreeOnly(false); }}
                title="Props where the AI pick disagrees with the market lean"
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  valueOnly
                    ? "bg-[#A855F7] text-white"
                    : "bg-[#141C2B] border border-[#A855F7]/40 text-[#C084FC] hover:bg-[#A855F7]/10"
                }`}
              >
                <Swords className="h-3.5 w-3.5" />
                {valueOnly ? "Showing value spots" : "Value spots only"}
                <span
                  className={`font-data text-[10px] rounded px-1 ${
                    valueOnly ? "bg-white/20 text-white" : "bg-[#A855F7]/15 text-[#C084FC]"
                  }`}
                >
                  {valueCount}
                </span>
              </button>
              <button
                data-testid="consensus-toggle"
                onClick={() => { setAgreeOnly((v) => !v); setValueOnly(false); }}
                title="Props where the AI pick and the market lean agree — higher-confidence bets"
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  agreeOnly
                    ? "bg-[#10B981] text-[#0B0F17]"
                    : "bg-[#141C2B] border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10"
                }`}
              >
                <Handshake className="h-3.5 w-3.5" />
                {agreeOnly ? "Showing agreement" : "AI + Market agree"}
                <span
                  className={`font-data text-[10px] rounded px-1 ${
                    agreeOnly ? "bg-black/20 text-[#0B0F17]" : "bg-[#10B981]/15 text-[#10B981]"
                  }`}
                >
                  {agreeCount}
                </span>
              </button>
              {agreeCount > 0 && (
                <button
                  data-testid="build-consensus-parlay-button"
                  onClick={buildConsensusParlay}
                  title="Auto-build the strongest AI + Market agreement parlay and load it into your slip"
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-[#0B0F17] hover:brightness-110"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  Build consensus parlay
                </button>
              )}
              {agreeCount > 0 && (
                <button
                  data-testid="open-triple-parlay-game"
                  onClick={() => setShowTriple(true)}
                  title="AI projection + most sportsbooks + AI trend read"
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors bg-gradient-to-r from-[#A78BFA] to-[#00E5FF] text-[#0B0F17] hover:brightness-110"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Triple-Check parlay
                </button>
              )}
              {agreeCount > 0 && (
                <button
                  data-testid="open-starters-parlay-game"
                  onClick={() => setShowStarters(true)}
                  title="Triple-Check legs ordered starters first, then bench"
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors bg-gradient-to-r from-[#F59E0B] to-[#3B82F6] text-[#0B0F17] hover:brightness-110"
                >
                  <Crown className="h-3.5 w-3.5" />
                  Starters-First parlay
                </button>
              )}
              {valueOnly && valueCount === 0 && (
                <p className="w-full text-[11px] text-slate-500 mt-0.5 font-data">
                  No value spots right now — the AI and market agree across the board.
                </p>
              )}
              {agreeOnly && agreeCount === 0 && (
                <p className="w-full text-[11px] text-slate-500 mt-0.5 font-data">
                  No agreement picks right now — the AI and market are split on these props.
                </p>
              )}
            </div>
            {compare.length === 1 && (
              <p className="text-xs text-[#00E5FF] mb-2">Pick one more prop to compare head-to-head…</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              {[
                { team: data.away_team, label: "Away", side: "left" },
                { team: data.home_team, label: "Home", side: "right" },
              ].map((col) => {
                const colProps = filtered.filter((p) => p.team_abbr === col.team.abbr);
                return (
                  <div key={col.team.abbr} data-testid={`team-column-${col.label.toLowerCase()}`}>
                    <div
                      className="flex items-center gap-2 mb-3 rounded-lg p-2 sticky top-16 z-10"
                      style={{ background: `linear-gradient(90deg, ${col.team.color}33, ${col.team.color}0d)`, boxShadow: `inset 3px 0 0 ${col.team.color}` }}
                    >
                      <TeamLogo sport={data.sport} abbr={col.team.abbr} color={col.team.color} size="md" />
                      <div className="min-w-0">
                        <div className="text-white font-semibold text-sm truncate">{col.team.name}</div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-wide font-data">{col.label} · {colProps.length} props</div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {colProps.map((p) => (
                        <PropCard
                          key={p.id}
                          prop={p}
                          injury={statusMap[p.player_name]}
                          live={liveStats[p.id]}
                          teamColor={col.team.color}
                          onCompare={toggleCompare}
                          compareActive={!!compare.find((c) => c.id === p.id)}
                          onOpenPlayer={onOpenPlayer}
                        />
                      ))}
                      {colProps.length === 0 && (
                        <p className="text-xs text-slate-500 text-center py-6 rounded-xl border border-dashed border-[#233248]">
                          No {metric === "ALL" ? "" : `${metric} `}props for {col.team.abbr}.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <GoalscorerSection props={goalProps} />
          </div>
        </div>
      )}

      <HeadToHeadModal propA={compare[0]} propB={compare[1]} open={h2hOpen} onClose={closeH2h} />
      <TripleParlayBuilder open={showTriple} onClose={() => setShowTriple(false)} matchupIds={[matchupId]}
        scopeLabel={data?.game_label} />
      <TripleParlayBuilder mode="starters" open={showStarters} onClose={() => setShowStarters(false)} matchupIds={[matchupId]}
        scopeLabel={data?.game_label} />
    </div>
  );
};
